import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { MEGA, NAV_FLAT } from "@/content/site";
import { ICONS } from "./icons";

function MegaPanel({ menu }) {
  return (
    <div className="mega" role="menu">
      <div className="mega-main">
        {menu.groups.map(group => (
          <div key={group.title} className="mega-group">
            <div className="mega-group-title">{group.title}</div>
            {group.items.map(item => {
              const Icon = ICONS[item.icon];
              return (
                <Link key={item.to} to={item.to} className="mega-item" role="menuitem">
                  <span className="mega-icon">{Icon && <Icon size={16} />}</span>
                  <strong>{item.label}</strong>
                  <span>{item.desc}</span>
                </Link>
              );
            })}
          </div>
        ))}
      </div>
      <div className="mega-aside">
        <div className="mega-aside-title">{menu.aside.title}</div>
        {menu.aside.items.map(item => <Link key={item.to} to={item.to} role="menuitem">{item.label}</Link>)}
      </div>
    </div>
  );
}

export default function SiteNav() {
  const { user } = useAuth();
  const [open, setOpen] = useState(null);
  const [stuck, setStuck] = useState(false);
  const navRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") setOpen(null); };
    const onClick = e => { if (navRef.current && !navRef.current.contains(e.target)) setOpen(null); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, []);

  // Petit délai à la sortie : traverser l'espace entre le bouton et le panneau ne ferme pas le menu.
  const enter = label => { clearTimeout(closeTimer.current); setOpen(label); };
  const leave = () => { closeTimer.current = setTimeout(() => setOpen(null), 120); };

  return (
    <header className={`site-nav${stuck ? " is-stuck" : ""}`} ref={navRef} onMouseLeave={leave}>
      <div className="site-nav-inner" style={{ position: "relative" }}>
        <Link to="/" className="site-nav-brand" aria-label="Perform, accueil"><Logo /></Link>

        <nav className="site-nav-items" aria-label="Navigation principale">
          {MEGA.map(menu => (
            <button
              key={menu.label}
              type="button"
              className="site-nav-trigger"
              data-open={open === menu.label}
              aria-expanded={open === menu.label}
              data-testid={`nav-${menu.label.toLowerCase()}`}
              onMouseEnter={() => enter(menu.label)}
              onFocus={() => enter(menu.label)}
              onClick={() => setOpen(open === menu.label ? null : menu.label)}
            >
              {menu.label}<ChevronDown size={14} />
            </button>
          ))}
          {NAV_FLAT.map(l => <Link key={l.to} to={l.to} onMouseEnter={() => enter(null)}>{l.label}</Link>)}
        </nav>

        <div className="site-nav-actions">
          {user
            ? <Link to="/dashboard" className="button button-dark" data-testid="nav-dashboard">Mon espace</Link>
            : <>
                <Link to="/auth" className="button button-outline" data-testid="nav-login">Se connecter</Link>
                <Link to="/auth" state={{ mode: "register" }} className="button button-dark" data-testid="nav-start">Commencer</Link>
              </>}
        </div>

        {open && <div onMouseEnter={() => enter(open)}><MegaPanel menu={MEGA.find(m => m.label === open)} /></div>}
      </div>
    </header>
  );
}
